import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard } from "@phosphor-icons/react";
import api from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

// Profile → Plan & billing: current plan, renewal date and the auto-renew switch.
export const BillingPanel = () => {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!sub) return null;

  const toggle = async () => {
    const on = !sub.auto_renew;
    if (!on && !window.confirm("Turn off auto-renew? You keep everything until the end of the period you've paid for.")) return;
    setBusy(true);
    try {
      await api.post("/billing/auto-renew", { on });
      toast.success(on ? "Auto-renew is back on" : "Auto-renew turned off — your plan ends on its end date");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update auto-renew"); } finally { setBusy(false); }
  };

  const free = !sub.tier || sub.tier === "free";
  return (
    <section className="bg-white rounded-3xl p-6 sm:p-8 border border-rf-ink/10" data-testid="billing-panel">
      <div className="flex items-center gap-2 mb-2">
        <CreditCard size={22} weight="duotone" className="text-rf-orange" />
        <h2 className="font-display text-2xl font-black text-rf-ink">Plan &amp; billing</h2>
      </div>
      {free ? (
        <p className="text-sm text-rf-ink-soft" data-testid="billing-free">
          You&apos;re on the free Taster plan. <Link to="/pricing" className="font-bold text-rf-orange hover:underline">See plans</Link> — from £2.08 a month.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-rf-ink" data-testid="billing-status">
            <b>{sub.label || "Paid plan"}</b>{sub.amount ? ` · £${sub.amount}` : ""} —{" "}
            {sub.expires_at ? (sub.auto_renew ? <>renews automatically on <b>{fmt(sub.expires_at)}</b></> : <>ends on <b>{fmt(sub.expires_at)}</b> (doesn&apos;t renew)</>) : "no end date"}
          </p>
          {sub.can_toggle ? (
            <button type="button" onClick={toggle} disabled={busy} data-testid="billing-auto-renew-toggle" className="btn-outline-ink !py-2 !px-4 text-sm">
              {sub.auto_renew ? "Turn off auto-renew" : "Turn auto-renew back on"}
            </button>
          ) : (
            <p className="text-xs text-rf-ink-soft">This plan was a one-off payment, so it won&apos;t renew. Choose a plan on <Link to="/pricing" className="font-bold underline">Pricing</Link> before it ends to keep going.</p>
          )}
        </div>
      )}
    </section>
  );
};
